/* eslint-env browser */
import React from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { useTranslation } from 'react-i18next';
import { showToast } from '../feedback';
import { tokens as c } from '../../theme';
import { compactPhone } from './tableUtils';

/**
 * Copy a value to the clipboard and say so ("Phone number copied"), or say
 * how to copy it by hand when the browser refuses.
 *
 *   copyText('+251964540448', t('phone', { defaultValue: 'Phone number' }), t)
 */
export const copyText = (text, what, t) => {
  const value = text === null || text === undefined ? '' : String(text);
  if (!value) return;
  const say = (key, def, opts) => (typeof t === 'function' ? t(key, { defaultValue: def, ...(opts || {}) }) : def.replace('{{what}}', (opts && opts.what) || ''));
  const ok = () => showToast.success(say('copied_value', '{{what}} copied', { what }));
  const fail = () => showToast.error(say('copy_failed', 'Could not copy. Select the text and copy it instead.'));
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(ok, fail);
      return;
    }
  } catch (e) {
    // fall through
  }
  fail();
};

/**
 * A small copy button beside a value (a phone number, a booking reference).
 * Inside a clickable table row it never opens the row. `phone` copies the
 * number without spaces (+251964540448), ready for MicroSIP.
 *
 *   <CopyButton value={row.mobile} phone what={t('phone', { defaultValue: 'Phone number' })} />
 *   <CopyButton value={booking.reference} what={t('booking_ref', { defaultValue: 'Booking reference' })} />
 */
export default function CopyButton({ value, what, phone = false, size = 'small', sx }) {
  const { t } = useTranslation();
  const text = phone ? compactPhone(value) : value === null || value === undefined ? '' : String(value);
  if (!text) return null;
  const name = what || t('value', { defaultValue: 'Value' });
  const label = t('copy_value', { what: name, defaultValue: 'Copy {{what}}' });
  return (
    <Tooltip title={label}>
      <IconButton
        size={size}
        aria-label={`${label}: ${text}`}
        onClick={(e) => {
          e.stopPropagation();
          copyText(text, name, t);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          color: c.INK_2,
          // A 44-px target (the owner's rule) around the small icon, without
          // making a table row taller.
          '&::after': { content: '""', position: 'absolute', inset: -8 },
          ...(sx || {}),
        }}
      >
        <ContentCopyRoundedIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );
}
